import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, ShieldCheckIcon, BarChart3Icon } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { GlobalStatsSkeleton } from '../components/SkeletonLoader';
import { useCryptoData } from '../hooks/useCryptoData';
import { useExchangeData } from '../hooks/useExchangeData';
import { useGlobalMetrics } from '../hooks/useGlobalMetrics';
export function HomePage() {
  const {
    data: cryptocurrencies
  } = useCryptoData(5);
  const {
    data: exchanges
  } = useExchangeData();
  const {
    metrics,
    loading: metricsLoading
  } = useGlobalMetrics();
  const topCryptos = cryptocurrencies.slice(0, 5);
  const topExchanges = exchanges.slice(0, 5);
  const formatLargeNumber = (num: number) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-id="element-511">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20" data-id="element-512">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-513">
          <div className="text-center max-w-3xl mx-auto" data-id="element-514">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6" data-id="element-515">
              <ShieldCheckIcon size={18} data-id="element-516" />
              <span className="text-sm font-medium" data-id="element-517">
                Trusted Crypto Intelligence
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-518">
              The TrustPilot for{' '}
              <span className="text-blue-400" data-id="element-519">Cryptocurrency</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-520">
              Comprehensive reviews, trust scores, and intelligence for
              cryptocurrencies and exchanges. Make informed decisions with
              verified data.
            </p>

            {/* Global Market Stats */}
            {metricsLoading ? <GlobalStatsSkeleton data-id="element-521" /> : metrics ? <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-522">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-523">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-524">
                    Total Market Cap
                  </div>
                  <div className="text-2xl font-bold" data-id="element-525">
                    {formatLargeNumber(metrics.totalMarketCap)}
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-526">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-527">24h Volume</div>
                  <div className="text-2xl font-bold" data-id="element-528">
                    {formatLargeNumber(metrics.total24hVolume)}
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20" data-id="element-529">
                  <div className="text-sm text-slate-300 mb-1" data-id="element-530">
                    BTC Dominance
                  </div>
                  <div className="text-2xl font-bold" data-id="element-531">
                    {metrics.btcDominance.toFixed(1)}%
                  </div>
                </div>
              </div> : null}

            <div className="flex flex-wrap gap-4 justify-center" data-id="element-532">
              <Link to="/cryptocurrencies" data-id="element-533">
                <Button size="lg" className="shadow-lg shadow-blue-500/20" data-id="element-534">
                  Explore Cryptocurrencies
                </Button>
              </Link>
              <Link to="/exchanges" data-id="element-535">
                <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-id="element-536">
                  View Exchanges
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-yellow-50 border-y border-yellow-200" data-id="element-537">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-538">
          <div className="flex items-start gap-3" data-id="element-539">
            <div className="flex-shrink-0 mt-0.5" data-id="element-540">
              <div className="w-1 h-full bg-yellow-500 rounded-full" data-id="element-541"></div>
            </div>
            <div className="flex-1" data-id="element-542">
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-543">
                <strong className="font-semibold text-gray-900" data-id="element-544">
                  Disclaimer:
                </strong>{' '}
                This website aggregates crypto-related information from various
                public sources. The content is provided for informational
                purposes only and does not constitute financial or investment
                advice. Always conduct independent research before making
                financial decisions.{' '}
                <Link to="/terms-of-use" className="text-blue-600 hover:text-blue-700 font-semibold underline" data-id="element-545">
                  Read full Terms
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-546">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-547">
          <Card className="p-6 text-center" hover data-id="element-548">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-549">
              <ShieldCheckIcon className="text-blue-600" size={24} data-id="element-550" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-551">Trust Scores</h3>
            <p className="text-slate-600" data-id="element-552">
              Proprietary trust scoring system based on security, transparency,
              and user reviews.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-553">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-554">
              <BarChart3Icon className="text-green-600" size={24} data-id="element-555" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-556">Live Market Data</h3>
            <p className="text-slate-600" data-id="element-557">
              Real-time pricing, market cap, volume, and performance metrics
              from CoinMarketCap.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-558">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-559">
              <TrendingUpIcon className="text-purple-600" size={24} data-id="element-560" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-561">Verified Reviews</h3>
            <p className="text-slate-600" data-id="element-562">
              Authentic user reviews from verified traders and investors in the
              crypto space.
            </p>
          </Card>
        </div>
      </section>

      {/* Top Cryptocurrencies Preview */}
      <section className="py-16 bg-slate-50" data-id="element-563">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-564">
          <div className="flex items-center justify-between mb-8" data-id="element-565">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-566">
              Top Cryptocurrencies
            </h2>
            <Link to="/cryptocurrencies" data-id="element-567">
              <Button variant="outline" data-id="element-568">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4" data-id="element-569">
            {topCryptos.map(crypto => <Link key={crypto.id} to={`/cryptocurrency/${crypto.id}`} data-id="element-570">
                <Card className="p-5" hover data-id="element-571">
                  <div className="flex items-center justify-between" data-id="element-572">
                    <div className="flex items-center gap-4" data-id="element-573">
                      <div className="flex items-center gap-3" data-id="element-574">
                        <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-575">
                          {crypto.rank}
                        </span>
                        <img src={crypto.logo} alt={crypto.name} className="w-10 h-10" data-id="element-576" />
                      </div>
                      <div data-id="element-577">
                        <div className="flex items-center gap-2 mb-1" data-id="element-578">
                          <h3 className="font-semibold text-lg" data-id="element-579">
                            {crypto.name}
                          </h3>
                          <span className="text-slate-500 text-sm" data-id="element-580">
                            {crypto.symbol}
                          </span>
                        </div>
                        <TrustScore score={crypto.trustScore} size="sm" data-id="element-581" />
                      </div>
                    </div>

                    <div className="text-right" data-id="element-582">
                      <div className="font-semibold text-lg mb-1" data-id="element-583">
                        $
                        {crypto.price >= 1 ? crypto.price.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }) : crypto.price.toFixed(6)}
                      </div>
                      <div className={`text-sm font-medium ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-584">
                        {crypto.change24h >= 0 ? '+' : ''}
                        {crypto.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Top Exchanges Preview */}
      <section className="py-16" data-id="element-585">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-586">
          <div className="flex items-center justify-between mb-8" data-id="element-587">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-588">Top Exchanges</h2>
            <Link to="/exchanges" data-id="element-589">
              <Button variant="outline" data-id="element-590">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-591">
            {topExchanges.map(exchange => <Link key={exchange.id} to={`/exchange/${exchange.id}`} data-id="element-592">
                <Card className="p-5" hover data-id="element-593">
                  <div className="flex items-center gap-3 mb-4" data-id="element-594">
                    <div className="flex items-center gap-2" data-id="element-595">
                      <span className="text-slate-500 font-semibold text-sm w-6" data-id="element-596">
                        {exchange.rank}
                      </span>
                      <img src={exchange.logo} alt={exchange.name} className="w-10 h-10" data-id="element-597" />
                    </div>
                    <div data-id="element-598">
                      <h3 className="font-semibold text-lg" data-id="element-599">{exchange.name}</h3>
                      <TrustScore score={exchange.trustScore} size="sm" data-id="element-600" />
                    </div>
                  </div>

                  <div className="space-y-2 text-sm" data-id="element-601">
                    <div className="flex justify-between" data-id="element-602">
                      <span className="text-slate-600" data-id="element-603">24h Volume:</span>
                      <span className="font-medium" data-id="element-604">
                        ${(exchange.tradingVolume / 1000000000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-605">
                      <span className="text-slate-600" data-id="element-606">Coins:</span>
                      <span className="font-medium" data-id="element-607">
                        {exchange.supportedCoins}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-608">
                      <span className="text-slate-600" data-id="element-609">Trading Fee:</span>
                      <span className="font-medium" data-id="element-610">{exchange.tradingFee}</span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white" data-id="element-611">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-612">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-613">
            Ready to Make Informed Decisions?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-614">
            Join thousands of investors who trust CryptoTopReviews for their
            crypto intelligence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-615">
            <Link to="/cryptocurrencies" data-id="element-616">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="element-617">
                Start Exploring
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}